import * as React from "react";
import {cx} from "emotion";

import "./homeowners-side-bar.scss";

import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {UseState}         from "@Common/react/use-state";
import {Static2}          from "@Common/react/static-2";
import {fragments}        from "@Common/react/fragments";
import {OnUpdated}        from "@Common/react/on-updated";
import {equalDeep}        from "@Common/utils/objects";
import {parseDateFormat}  from "@Common/utils/date/parse-date-format";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";

import {SelectTargetSection} from "./select-target-section/select-target-section";
import {DatesSection}        from "./dates-section/dates-section";
import {ResultsSection}      from "./results-section/results-section";
import {groupSections}       from "../../common/group-sections";

export const HomeownersSideBar = ({viewSize, searchRequest, searchResult, renderErrorNoti, next}) => cs(
    consumeContext("apis"),
    ["selectTargetSection", ({}, next) => SelectTargetSection({next})],
    ["datesSection", ({}, next) => DatesSection({next})],
    ["resultsSection", ({}, next) => ResultsSection({zipCodes: searchResult.value?.zipCodes, next})],
    ["eh", ({}, next) => ErrorHighlighter({next})],
    ["show", (_, next) => UseState({initValue: true, next})],
    ["showErrors", ({}, next) => UseState({initValue: [], next})],
    ["groupedSections", ({selectTargetSection, datesSection}, next) => next(groupSections([selectTargetSection, datesSection]))],
    ["scrollRef", (_, next) => Static2({next})],
    ["nextRef", (_, next) => Static2({next})],
    ["stuck", (_, next) => UseState({initValue: false, next})],
    ({apis, show, showErrors, selectTargetSection, datesSection, resultsSection, groupedSections, scrollRef, nextRef, stuck, eh}) => next({
        customQty: resultsSection?.customQty ? resultsSection.customQty : null,
        getNextAction: ({onNext}) => ({
            disabled: !searchResult.value || searchResult.value.quantity <= 0 || !resultsSection.valid,
            action: async () => {
                if (!searchResult.value || searchResult.value.quantity <= 0) {
                    if (!showErrors.value.length) {
                        showErrors.onChange(["all"]);
                    }
                    eh.start();
                } else if (!resultsSection.valid) {
                    if (!showErrors.value.length) {
                        showErrors.onChange(["all"]);
                    }
                    resultsSection.highlightErrors();
                } else {
                    onNext();
                }
            },
        }),
        render: () => {
            const onScroll = () => {
                const scrollContainer = scrollRef.get();
                const contentBottom = scrollContainer?.getBoundingClientRect().bottom;
                const nextBottom = nextRef.get()?.getBoundingClientRect().bottom;
                const scrollIsBottom = scrollContainer?.scrollHeight - scrollContainer?.scrollTop === scrollContainer?.clientHeight;
                const newStuck = nextBottom == contentBottom && !scrollIsBottom;
                if (newStuck !== stuck.value) {
                    stuck.onChange(nextBottom == contentBottom && !scrollIsBottom);
                }
            };

            return fragments(
                (
                    <div className={cx("homeowners-side-bar-t83", {disabled: !show.value})}>
                        <div {...{
                            className: "toggle",
                            "xd-qa-name": "New Homeowners Toggle",
                            "xd-qa-type": "button",
                            onClick: () => show.onChange(!show.value)
                        }}>
                            <span>{show.value ? "<" : ">"}</span>
                        </div>
                        <div className="sidebar">
                            <div
                                className={cx("content", viewSize)}
                                ref={scrollRef.set}
                                onScroll={onScroll}
                            >
                                <div className={cx("selections", eh.css)}>
                                    {selectTargetSection.render({
                                        unplottedZips: searchResult.value?.zipCodes?.filter(zip => !zip.polygon),
                                        showErrors
                                    })}
                                    {datesSection.render({resultsRendered: searchResult.value, showErrors})}
                                    
                                    {cs(
                                        ["submitting", (_, next) => UseState({next})],
                                        ({submitting}) => {
                                            const request = {
                                                ...selectTargetSection.getSearchRequest(),
                                                ...datesSection.dates,
                                            };
                                            const disabled = (
                                                !groupedSections.valid ||
                                                equalDeep(searchRequest.value, request)
                                            );
                                            return (
                                                <div 
                                                    className={cx("button-container", {stuck: stuck.value})}
                                                    ref={nextRef.set}
                                                >
                                                    <button {...{
                                                        className: cx("next", {
                                                            disabled,
                                                        }),
                                                        disabled: submitting.value,
                                                        onClick: async () => {
                                                            if (!groupedSections.valid) {
                                                                if (!showErrors.value.length) {
                                                                    showErrors.onChange(["all"]);
                                                                }
                                                                groupedSections.highlightErrors();
                                                            } else if (!disabled) {
                                                                if (!!showErrors.value.length) {
                                                                    showErrors.onChange([]);
                                                                }
                                                                submitting.onChange(true);
                                                                searchResult.onChange(null);
                                                                searchRequest.onChange(null);
                                                                groupedSections.clearApiError();
                                                                try {
                                                                    const df = parseDateFormat("yyyy-mm-dd");
                                                                    const response = await apis.next.mailing.homeowners.searchHomeowners({
                                                                        startDate: df.formatDate(request.startDate),
                                                                        endDate: df.formatDate(request.endDate),
                                                                        zipCodes: request.zipCodes
                                                                    });
                                                                    if (response.result === "failure") {
                                                                        if (response.errors?.msg) {
                                                                            throw response.errors.msg
                                                                        }
                                                                        alert("Unknown error");
                                                                    } else {
                                                                        searchResult.onChange(response);
                                                                        searchRequest.onChange(request);
                                                                        resultsSection.ref.get().scrollIntoView({behavior: 'smooth', block: 'end'});
                                                                    }
                                                                } catch (error) {
                                                                    groupedSections.handleApiError(error);
                                                                } finally {
                                                                    submitting.onChange(false);
                                                                }
                                                            }
                                                        },
                                                        "xd-qa-type": "button",
                                                        "xd-qa-name": "New Homeowners View Results",
                                                    }}>
                                                        {!submitting.value ? (
                                                            "View Results"
                                                        ) : (
                                                            "Searching..."
                                                        )}
                                                    </button>
                                                </div>
                                            )
                                        }
                                    )}
                                </div>
                                {searchResult.value && resultsSection.render({showErrors})}
                            </div>
                        </div>
                    </div>
                ),
                OnUpdated({
                    action: onScroll,
                }),
            )
        }
    })
);

